<template>
  <div class="editor-section">
    <div class="tab-container">
      <div
        v-for="(tab, index) in tabs"
        :key="index"
        class="tab-section"
        :class="{ active: activeTabIndex === index }"
        @click="switchTab(index)"
        @dblclick="startEditingTab(index)"
      >
        <span v-if="editingTabIndex !== index">{{ tab.name }}</span>
        <input
          v-else
          :ref="
            (el) => {
              tabInputRefs[index] = el;
            }
          "
          v-model="tab.name"
          @blur="finishEditingTab"
          @keyup.enter="finishEditingTab"
          type="text"
          class="tab-edit-input"
          @click.stop
        />
        <button
          @click.stop="removeTab(index)"
          class="tab-remove-btn"
          title="Remove Tab"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <div class="tab-add-container">
        <button class="tab-add" @click="handleAddTab" title="Add New Tab">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </button>
      </div>
    </div>

    <!-- Tab type selection modal -->
    <div
      v-if="showTabTypeModal"
      class="tab-type-modal-overlay"
      @click="showTabTypeModal = false"
    >
      <div class="tab-type-modal" @click.stop>
        <div class="modal-header">
          <h3>Select Tab Type</h3>
          <button class="close-modal-btn" @click="showTabTypeModal = false">
            ×
          </button>
        </div>
        <div class="modal-body">
          <div class="tab-type-option" @click="addNewTab('module')">
            <div class="tab-type-icon module">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
              </svg>
            </div>
            <div class="tab-type-info">
              <h4>{{ config.moduleTabLabel || "Module" }}</h4>
              <p>Rich text editor with sections for content</p>
            </div>
          </div>

          <div
            class="tab-type-option"
            @click="addNewTab('assessment')"
            v-if="config.enableAssessment && !hasAssessmentTab"
          >
            <div class="tab-type-icon assessment">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="9 11 12 14 22 4"></polyline>
                <path
                  d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                ></path>
              </svg>
            </div>
            <div class="tab-type-info">
              <h4>Assessment</h4>
              <p>Structured assessment items with accordion format</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="tabs.length > 0 && activeTabIndex >= 0 && tabs[activeTabIndex]"
      class="content-container"
    >
      <div class="duration-input-container">
        <input
          v-model="tabs[activeTabIndex].duration"
          @input="formatDurationInput($event, activeTabIndex)"
          type="text"
          placeholder="Duration (minutes)"
          class="duration-input"
        />
      </div>

      <!-- Module type content -->
      <template v-if="tabs[activeTabIndex].type === 'module'">
        <div
          v-for="(section, sectionIndex) in tabs[activeTabIndex].sections"
          :key="`section-${activeTabIndex}-${sectionIndex}`"
          class="section"
        >
          <div class="section-header">
            <button
              v-if="sectionIndex > 0"
              @click="removeSection(activeTabIndex, sectionIndex)"
              class="remove-section-btn"
              title="Remove Section"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="3 6 5 6 21 6"></polyline>
                <path
                  d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                ></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
              <span>Remove Section</span>
            </button>
          </div>

          <TipTapToolbar
            v-if="section.editor"
            :editor="section.editor"
            :editorId="`editor-${activeTabIndex}-${sectionIndex}`"
          />

          <div class="tiptap-wrapper">
            <editor-content
              :editor="section.editor"
              :id="`editor-${activeTabIndex}-${sectionIndex}`"
            />
          </div>

          <div class="section-footer">
            <button
              @click="addNestedSection(activeTabIndex)"
              class="add-section-btn"
              title="Add Section Below"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              <span>Add Section</span>
            </button>
          </div>
        </div>
      </template>

      <!-- Assessment type content -->
      <template
        v-else-if="
          tabs[activeTabIndex].type === 'assessment' && config.enableAssessment
        "
      >
        <AssessmentSection
          ref="assessmentSectionRef"
          :initialAssessments="tabs[activeTabIndex].assessments"
          @assessments-updated="updateAssessments"
        />
      </template>
    </div>
    <div v-else class="content-container empty-state">
      <div class="empty-state-content">
        <div class="empty-state-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="9" y1="9" x2="15" y2="15"></line>
            <line x1="15" y1="9" x2="9" y2="15"></line>
          </svg>
        </div>
        <h3>
          No {{ config.moduleTabLabel?.toLowerCase() || "sections" }} available
        </h3>
        <p>
          {{
            tabs.length === 0
              ? "Get started by adding your first " +
                (config.moduleTabLabel?.toLowerCase() || "section") +
                "."
              : "Select an existing tab or add a new one."
          }}
        </p>
        <button
          v-if="tabs.length === 0"
          @click="handleAddTab"
          class="empty-state-button"
        >
          Add First {{ config.moduleTabLabel || "Section" }}
        </button>
      </div>
    </div>

    <div class="attachment-buttons" v-if="config.enableAttachments">
      <button
        class="attachment-button"
        @click="openSlideModal"
        v-if="config.enableSlides"
      >
        <img src="/images/icon/link.svg" alt="Slide Deck" />
        {{ slidesAttached ? "Change Slide Deck" : "Attach Slide Deck" }}
      </button>

      <!-- Visual indicator for attached slides -->
      <div
        v-if="slidesAttached && config.enableSlides"
        class="slides-attached-indicator"
      >
        <div class="indicator-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        </div>
        <a :href="attachedSlide?.url" target="_blank" class="indicator-text">
          <span class="indicator-title">Slides Attached</span>
        </a>
      </div>

      <!-- Instructional Materials Section -->
      <div
        class="instructional-materials-section"
        v-if="config.enableMaterials"
      >
        <div
          v-for="(button, index) in materialButtons"
          :key="`material-btn-${index}`"
          class="material-button-container"
        >
          <button
            class="instructional-attachment-button"
            @click="openMaterialsModal(index)"
          >
            <img
              src="/images/icon/document-upload.svg"
              alt="Instructional Material"
            />
            {{
              hasMaterialAt(index)
                ? "Change Instructional Material"
                : "Attach Instructional Material"
            }}
          </button>

          <!-- Show remove button only if there's material attached or if not the first button -->
          <button
            v-if="hasMaterialAt(index) || index > 0"
            class="remove-material-button"
            @click="
              index === 0
                ? removeMaterialOnly(index)
                : removeMaterialButton(index)
            "
            title="Remove this button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <!-- Add button only for the first item -->
          <button
            v-if="index === 0"
            class="add-material-button"
            @click="addNewMaterialButton"
            title="Add another material button"
          >
            <img src="/images/icon/add-square.svg" alt="Add Material" />
          </button>
          <!-- Material link indicators -->

          <div class="material-link-indicator" v-if="hasMaterialAt(index)">
            <div class="indicator-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <a
              :href="getMaterialUrl(index)"
              target="_blank"
              class="indicator-text"
            >
              <span class="indicator-title">{{ getMaterialTitle(index) }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
    <!-- Add SlideModal component -->
    <SlideModal
      v-if="config.enableSlides"
      :show="showSlideModal"
      :available-tabs="directSlideModalTabs"
      :activeTabIndex="activeTabIndex"
      :is-changing="isChangingSlides"
      :current-slide-url="attachedSlide?.url"
      @close="closeSlideModal"
      @insert="handleSlideInsert"
      :module-type="props.config.moduleType"
    />
    <!-- Add Materials Modal component -->
    <MaterialsModal
      v-if="config.enableMaterials"
      :show="showMaterialsModal"
      :editing-index="editingMaterialIndex"
      :current-material="editingMaterial"
      :is-edit-form="props.isEditForm"
      @close="closeMaterialsModal"
      @add="addMaterial"
      @update="updateMaterial"
      :module-type="props.config.moduleType"
    />
  </div>
</template>

<script setup>
/* eslint-disable no-unused-vars */
import { EditorContent } from "@tiptap/vue-3";
import {
  defineEmits,
  defineExpose,
  defineProps,
  onBeforeUnmount,
  onMounted,
  watch,
} from "vue";
import AssessmentSection from "./AssessmentSection.vue";
import TipTapToolbar from "../lesson-plans/TipTapToolbar.vue";
import MaterialsModal from "./MaterialsModal.vue";
import SlideModal from "./SlideModal.vue";

const props = defineProps({
  isEditForm: {
    type: Boolean,
    default: false,
  },
  config: {
    type: Object,
    default: () => ({
      enableAssessment: true,
      enableAttachments: true,
      enableSlides: true,
      enableMaterials: true,
      moduleTabLabel: "Module",
      defaultTabStructure: "lesson-plan", // 'lesson-plan' or 'professional-development'
      initialPlaceholder: "Start writing your content here...",
    }),
  },
});

const emit = defineEmits(["tabs-updated"]);

// Use the composable
import { useBaseEditor } from "./composables/useBaseEditor.js";

const {
  // State
  tabs,
  activeTabIndex,
  editingTabIndex,
  tabInputRefs,
  assessmentSectionRef,
  showTabTypeModal,
  hasAssessmentTab,
  directSlideModalTabs,

  // Slides
  showSlideModal,
  slidesAttached,
  attachedSlide,
  isChangingSlides,

  // Materials
  showMaterialsModal,
  editingMaterialIndex,
  editingMaterial,
  instructionalMaterials,
  materialButtons,

  // Methods
  createEditor,
  createEditorWithJSON,
  initializeEditors,
  destroyEditor,
  switchTab,
  handleAddTab,
  addNewTab,
  removeTab,
  startEditingTab,
  finishEditingTab,
  addNestedSection,
  removeSection,
  updateAssessments,
  formatDurationInput,
  emitTabsUpdated,
  updateFromParent,

  // Slides methods
  openSlideModal,
  closeSlideModal,
  handleSlideInsert,
  insertContent,
  setSlideAttachedState,
  isSlideAttached,
  getSlideData,

  // Materials methods
  hasMaterialAt,
  getMaterialUrl,
  getMaterialTitle,
  addNewMaterialButton,
  removeMaterialOnly,
  removeMaterialButton,
  openMaterialsModal,
  closeMaterialsModal,
  addMaterial,
  updateMaterial,
  setInstructionalMaterials,
  getInstructionalMaterials,
} = useBaseEditor(props.config, emit);

onMounted(() => {
  tabInputRefs.value.length = tabs.value.length;
  initializeEditors();
  emitTabsUpdated();
});

onBeforeUnmount(() => {
  tabs.value.forEach((tab) => {
    if (tab.type === "module" && tab.sections) {
      tab.sections.forEach((section) => {
        destroyEditor(section.editor);
      });
    }
  });
});

watch(
  tabs,
  (newTabs) => {
    if (tabInputRefs.value.length !== newTabs.length) {
      tabInputRefs.value.length = newTabs.length;
    }
  },
  { deep: true }
);

defineExpose({
  updateFromParent,
  openSlideModal,
  insertContent,
  getSlideData,
  isSlideAttached,
  setSlideAttachedState,
  getInstructionalMaterials,
  setInstructionalMaterials,
});
</script>

<style scoped>
.editor-section {
  display: flex;
  flex-direction: column;
  gap: 0;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.attachment-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-right: auto;
  margin-top: 20px;
}

.attachment-button {
  display: flex;
  align-items: center;
  width: fit-content;
  gap: 8px;
  padding: 8px 16px;
  background-color: var(--primary-blue);
  color: white;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.instructional-attachment-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid var(--primary-blue);
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  color: var(--primary-blue);
  transition: all 0.2s ease;
  background: white;
}

.instructional-attachment-button:hover {
  background-color: var(--primary-blue);
  color: white;
}

.instructional-attachment-button:hover img {
  filter: brightness(0) invert(1);
}

.attachment-button svg {
  color: var(--primary-blue, #0080ff);
}

.tab-container {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: -1px;
  align-items: flex-end;
}

.tab-section {
  background: #e9ecef;
  color: #495057;
  padding: 10px 15px;
  border-radius: 10px 10px 0 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease-in-out;
  border: 1px solid #dee2e6;
  border-bottom: none;
  font-size: 0.95em;
}

.tab-section:hover {
  background: #dee2e6;
  color: var(--primary-blue);
}

.tab-section.active {
  background: var(--primary-blue);
  color: #ffffff;
  border-color: #dee2e6 #dee2e6 #ffffff;
  font-weight: 600;
  z-index: 2;
}

.tab-type-badge {
  font-size: 0.7em;
  padding: 2px 6px;
  border-radius: 10px;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.tab-type-badge.module {
  background-color: #e6f7ff;
  color: #0077cc;
}

.tab-type-badge.assessment {
  background-color: #fff2e8;
  color: #fa541c;
}

.tab-section.active .tab-type-badge.module {
  background-color: #0077cc;
  color: #ffffff;
}

.tab-section.active .tab-type-badge.assessment {
  background-color: #fa541c;
  color: #ffffff;
}

.tab-remove-btn {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  margin-left: 5px;
  cursor: pointer;
  color: #6c757d;
  line-height: 1;
  transition: color 0.2s ease;
  display: inline-flex;
  align-items: center;
}

.tab-section.active .tab-remove-btn {
  color: #ffffff;
}

.tab-remove-btn:hover {
  color: #dc3545;
}

.tab-remove-btn svg {
  display: block;
}

.tab-edit-input {
  border: none;
  background: rgba(255, 255, 255, 0.8);
  outline: 1px solid var(--primary-blue);
  padding: 4px 8px;
  border-radius: 4px;
  width: 120px;
  font: inherit;
  margin-right: 5px;
}

.tab-add-container {
  display: flex;
  align-items: center;
}

.tab-add {
  background: #f8f9fa;
  color: var(--primary-blue);
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s ease;
  border: 1px dashed #adb5bd;
  margin-left: 5px;
  display: inline-flex;
  align-items: center;
}

.tab-add:hover {
  background: #e9ecef;
  border-color: var(--primary-blue);
}
.tab-add svg {
  display: block;
}

.content-container {
  border: 1px solid #dee2e6;
  border-radius: 0 6px 6px 6px;
  padding: 25px;
  background: #ffffff;
  min-height: 350px;
  z-index: 1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03) inset;
}

.content-container.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-style: italic;
}

.empty-state-content {
  text-align: center;
  max-width: 400px;
}

.empty-state-icon {
  color: #adb5bd;
  margin-bottom: 16px;
}

.empty-state-content h3 {
  color: #495057;
  font-weight: 600;
  margin: 0 0 8px 0;
  font-style: normal;
}

.empty-state-content p {
  color: #6c757d;
  margin: 0 0 20px 0;
  font-style: normal;
  line-height: 1.5;
}

.empty-state-button {
  background: var(--primary-blue);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.empty-state-button:hover {
  background: #0056b3;
  transform: translateY(-1px);
}

.section {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e9ecef;
}
.section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  font-size: 0.9em;
  gap: 15px;
}

.duration-input-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.duration-input {
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 8px 12px;
  width: 180px;
  color: #495057;
  font-size: 0.9em;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}
.duration-input:focus {
  border-color: var(--primary-blue);
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.tiptap-wrapper {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 10px 15px;
  min-height: 180px;
  background: #ffffff;
  margin-top: -1px;
}

/* Editor-specific styles - content styles are now handled globally in tiptap-editor.css */

.section-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
}

.add-section-btn,
.remove-section-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #495057;
}

.add-section-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
  color: #212529;
}
.add-section-btn svg {
  color: var(--primary-blue);
}

.remove-section-btn {
  background: #fff6f6;
  border-color: #f5c6cb;
  color: #dc3545;
}
.remove-section-btn:hover {
  background: #f8d7da;
  border-color: #f1b0b7;
  color: #b02a37;
}
.remove-section-btn svg {
  color: #dc3545;
}
.remove-section-btn span,
.add-section-btn span {
  line-height: 1;
}

/* Table Styles */
.tiptap-wrapper :deep(table) {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 1rem 0;
  overflow: hidden;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.tiptap-wrapper :deep(th),
.tiptap-wrapper :deep(td) {
  padding: 0.75em 1em;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;
  min-width: 1em;
  border: 1px solid #dee2e6;
}

/* Normalize font properties for table cells to match paragraphs */
.tiptap-wrapper :deep(td) {
  font-size: 1rem !important;
  font-weight: normal !important;
  line-height: 1.6 !important;
  font-family: inherit !important;
}

.tiptap-wrapper :deep(table.default-table) {
  border: 1px solid #dee2e6;
}

.tiptap-wrapper :deep(table.default-table th),
.tiptap-wrapper :deep(table.default-table td) {
  border: 1px solid #dee2e6;
}

.tiptap-wrapper :deep(table.borderless-table) {
  border: none;
}

.tiptap-wrapper :deep(table.borderless-table th),
.tiptap-wrapper :deep(table.borderless-table td) {
  border: none;
  border-bottom: 1px solid #f1f3f5;
}

.tiptap-wrapper :deep(th) {
  background-color: #f1f3f5;
  font-weight: 600;
  text-align: left;
}

/* Additional table styles for consistent spacing */
.ProseMirror table {
  margin: 1rem 0;
}

/* Styles handled globally in tiptap-editor.css */

/* Column resizing styles */
.tiptap-wrapper :deep(.resize-cursor) {
  cursor: col-resize;
}

.tiptap-wrapper :deep(.selectedCell:after) {
  z-index: 2;
  position: absolute;
  content: "";
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(173, 216, 230, 0.3);
  pointer-events: none;
}

.tiptap-wrapper :deep(.column-resize-handle) {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: -2px;
  width: 4px;
  background-color: var(--primary-blue);
  pointer-events: none;
  z-index: 20;
}

/* Tab Type Modal */
.tab-type-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tab-type-modal {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 500px;
  max-width: 90%;
  max-height: 90%;
  overflow: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h3 {
  margin: 0;
  font-weight: 600;
  color: #212529;
}

.close-modal-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
}

.modal-body {
  padding: 20px;
}

.tab-type-option {
  display: flex;
  align-items: center;
  padding: 15px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  margin-bottom: 15px;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-type-option:last-child {
  margin-bottom: 0;
}

.tab-type-option:hover {
  background-color: #f8f9fa;
  border-color: #ced4da;
}

.tab-type-icon {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  flex-shrink: 0;
}

.tab-type-icon.module {
  background-color: #e6f7ff;
  color: #0077cc;
}

.tab-type-icon.assessment {
  background-color: #fff2e8;
  color: #fa541c;
}

.tab-type-info {
  flex-grow: 1;
}

.tab-type-info h4 {
  margin: 0 0 5px 0;
  font-weight: 600;
  color: #212529;
}

.tab-type-info p {
  margin: 0;
  color: #6c757d;
  font-size: 0.9em;
}

/* Materials List Styles */
.materials-list {
  margin-top: 15px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.material-item {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s ease;
  background-color: #f8f9fa;
}

.material-item:hover {
  background-color: #f1f3f5;
}

.material-item:last-child {
  border-bottom: none;
  border-radius: 0 0 4px 4px;
}

.material-item:first-child {
  border-radius: 4px 4px 0 0;
}

.material-icon {
  width: 24px;
  height: 24px;
  margin-right: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.material-type-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.material-info {
  flex-grow: 1;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.material-info:hover {
  background-color: rgba(0, 128, 255, 0.1);
}

.material-title {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: var(--primary-blue);
  font-weight: 500;
}

.material-remove-btn {
  background: none;
  border: none;
  padding: 8px;
  margin-left: 10px;
  cursor: pointer;
  color: #6c757d;
  line-height: 1;
  transition: all 0.2s ease;
  border-radius: 4px;
}

.material-remove-btn:hover {
  color: #dc3545;
  background-color: rgba(220, 53, 69, 0.1);
}

.material-remove-btn svg {
  display: block;
}

.add-material-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8f9fa;
  border: none;
  padding: 8px 12px;
  margin: 0;
  width: 100%;
  cursor: pointer;
  color: var(--primary-blue);
  font-weight: 500;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.add-material-btn:hover {
  background-color: #e9ecef;
  color: var(--primary-green);
}

.material-button-container {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.add-material-button,
.remove-material-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-material-button img {
  width: 20px;
  height: 20px;
}

.add-material-button:hover {
  background-color: #e9ecef;
  border-color: var(--primary-blue);
}

.remove-material-button:hover {
  background-color: #ffeeee;
  border-color: #dc3545;
}

.button-actions {
  display: flex;
  gap: 4px;
}

.material-link-indicator {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f5f9ff;
  border: 1px solid #e6f0ff;
  border-radius: 6px;
}

.indicator-icon {
  margin-right: 8px;
  color: #0077cc;
}

.indicator-text {
  text-decoration: none;
  color: #0077cc;
  font-size: 0.9em;
}

.indicator-text:hover {
  text-decoration: underline;
}

.indicator-title {
  font-weight: 500;
}

.instructional-materials-section {
  display: flex;
  flex-direction: column;
}

.slides-attached-indicator {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f5f9ff;
  border: 1px solid #e6f0ff;
  border-radius: 6px;
}
</style>
